import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { validateDocumentValues } from '@worki/shared';
import { DigitalDocumentView } from '../components/DigitalDocumentView.js';
import type { DigitalDocumentValues, PublicSignatureDocument } from '@worki/shared';
import * as platform from '@platform';
import { apiHeaders, RequestError } from '../lib/api.js';
import { apiUrl } from '../lib/config.js';
import { asksSignerToType } from '../lib/anchors.js';
import { Button, Card, ErrorNotice, Field, IconButton, Input, LoadingScreen } from '../components/ui.js';
import { LanguageSwitcher } from '../components/LanguageSwitcher.js';
import { Modal } from '../components/Modal.js';
import { SignaturePlacer } from '../components/SignaturePlacer.js';
import { importSigningImage, signingImage } from './signing/SignatureAssets.js';
import { SignaturePad } from '../components/SignaturePad.js';
import { currentLocale } from '../i18n.js';

/**
 * SIG-002 — the page the customer signs on.
 *
 * Unauthenticated, like the quote link, and for the same reason: whoever has to
 * sign has no account here. The whole document is on the page above the pad,
 * because a signature collected under a summary is a signature on something
 * nobody read — the words being agreed to have to be the words on screen.
 */

async function publicRequest<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(apiUrl(`/public${path}`), {
      credentials:'include',
      ...init,
      headers: await apiHeaders({ 'Content-Type': 'application/json', ...(init?.headers ?? {}) }),
    });
  } catch {
    throw new RequestError(0, 'errors.network');
  }

  const payload: unknown = response.headers.get('content-type')?.includes('application/json')
    ? await response.json()
    : null;

  if (!response.ok) {
    const error = (payload as { error?: { code?: string; fields?: Record<string, string> } } | null)?.error;
    throw new RequestError(response.status, error?.code ?? 'errors.unknown', error?.fields);
  }
  return payload as T;
}

/**
 * Who is asking for the signature. Exported so it can be rendered on its own:
 * the page around it needs a router, a query client and a live link.
 */
export function SigningBusinessHeader({ business }: { business: PublicSignatureDocument['business'] }) {
  const { t } = useTranslation();
  return (
    <Card>
      <div className="public-header">
        {business.logoUrl && <img className="public-logo" src={business.logoUrl} alt={business.name} />}
        <div>
          <h1>{business.name}</h1>
          <p className="muted ltr-nums">{[business.phone, business.email].filter(Boolean).join(' · ')}</p>
          {/* 2.5 — its own line rather than a third item on the one above: that
              line is set left to right for the phone and the email, and a
              Hebrew label inside it would read after its own number. */}
          {business.taxId && (
            <p className="muted">
              {t('profile.taxId')}: <span className="ltr-nums">{business.taxId}</span>
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}

/**
 * 1.50 — the picture under the pad. Picked, it waits for "שמור" to put it in
 * the boxes; saved, it is in them beside the drawing. The X takes back
 * whichever it is: a waiting picture is dropped, a saved one comes out of the
 * boxes.
 */
export function PickedMark({ image, waiting, disabled, onSave, onRemove }: {
  image: string;
  waiting: boolean;
  disabled: boolean;
  onSave: () => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="row row--wrap">
      <img src={image} alt={t('publicSign.pickedMark')} className="h-12 max-w-40 rounded border border-border bg-white object-contain" />
      {waiting && (
        <Button type="button" disabled={disabled} onClick={onSave}>
          {t('publicSign.saveMark')}
        </Button>
      )}
      <IconButton type="button" label={t('publicSign.removeMark')} disabled={disabled} onClick={onRemove}>
        <X className="h-[18px] w-[18px]" aria-hidden />
      </IconButton>
    </div>
  );
}

export function PublicSignPage() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const queryClient = useQueryClient();
  // Keep the document as the only page scroller. A fixed-height body with
  // overflow-x:hidden creates an extra vertical scrollport in mobile Chromium.
  useEffect(() => {
    window.document.documentElement.classList.add('public-sign-route');
    return () => window.document.documentElement.classList.remove('public-sign-route');
  }, []);


  const [fieldValues, setFieldValues] = useState<DigitalDocumentValues>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [anchorTexts, setAnchorTexts] = useState<Record<string, string>>({});
  const [padVersion, setPadVersion] = useState(0);
  useEffect(() => { setFieldValues({}); setFieldErrors({}); setSignerName(''); setSignature(null); setUploaded(null); setPicked(null); setPickNote(''); setAnchorTexts({}); }, [token]);

  const [signerName, setSignerName] = useState('');
  /* 1.50 — what is drawn on the pad, and the picture that was put in the boxes
     with "שמור": kept apart, and one image once they are sent. */
  const [signature, setSignature] = useState<string | null>(null);
  const [stylingSignature, setStylingSignature] = useState(false);
  const [uploaded, setUploaded] = useState<string | null>(null);
  /* A picture read and waiting for "שמור", and what the signer is told while it is read. */
  const [picked, setPicked] = useState<string | null>(null);
  const [pickNote, setPickNote] = useState('');
  const [preparingAssets, setPreparingAssets] = useState(false);
  /* What the row under the pad shows: the picture waiting for "שמור", else the saved one. */
  const shownMark = picked ?? uploaded;
  /* The image the boxes on the document show: the one that would be sent. */
  const [preview, setPreview] = useState<string | null>(null);
  useEffect(() => {
    let current = true;
    setStylingSignature(true);
    void signingImage(signature, uploaded).then(
      (image) => { if (current) setPreview(image); },
      () => { if (current) setPreview(null); },
    ).finally(() => { if (current) setStylingSignature(false); });
    return () => { current = false; };
  }, [signature, uploaded]);
  const [blockedToken, setBlockedToken] = useState<string | null>(null);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const [waitingNotice, setWaitingNotice] = useState(false);
  const openedTokens = useRef(new Set<string>());
  useEffect(() => setWaitingNotice(false), [token]);

  const documentQuery = useQuery({
    queryKey: ['public-sign', token],
    queryFn: async () => {
      const document = await publicRequest<PublicSignatureDocument>(`/sign/${token}${openedTokens.current.has(token!) ? '/status' : ''}`);
      openedTokens.current.add(token!);
      return document;
    },
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: query => {
      const document = query.state.data;
      return document && !document.expired && (document.status === 'sent' || document.status === 'viewed') ? 5000 : false;
    },
  });

  const sign = useMutation({
    mutationFn: async () =>
      publicRequest<PublicSignatureDocument>(`/sign/${token}`, {
        method: 'POST',
        body: JSON.stringify({ expectedRevision: documentQuery.data?.contentRevision, fieldValues, anchorTexts, signerName: signerName.trim(), signature: preview }),
      }),
    onSuccess: document => {
      queryClient.setQueryData(['public-sign', token], document);
      setWaitingNotice(document.signers.some(signer => !signer.signedAt));
      void queryClient.invalidateQueries({ queryKey: ['public-sign', token] });
    },
    onError: (error: RequestError) => setFieldErrors(Object.fromEntries(Object.entries(error.fields).filter(([key]) => key.startsWith('fieldValues.')).map(([key, value]) => [key.slice(12), value]))),
  });

  /*
   * SIG-004 — the other answer.
   *
   * A recipient who will not sign had nowhere to say so, so the only way to
   * refuse was to close the tab — which reads on the sender's side as a
   * document that was never opened, and gets chased on a reminder clock that
   * never stops. The reason is optional: a refusal that will not go through
   * without an explanation is a refusal that never gets sent.
   */
  const decline = useMutation({
    mutationFn: () =>
      publicRequest<PublicSignatureDocument>(`/sign/${token}/decline`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason.trim() || null, expectedRevision: documentQuery.data?.contentRevision }),
      }),
    onSuccess: () => {
      setDeclining(false);
      void queryClient.invalidateQueries({ queryKey: ['public-sign', token] });
    },
  });

  const reloadLatest = () => {
    setFieldValues({}); setFieldErrors({}); setAnchorTexts({}); setSignature(null); setUploaded(null); setPicked(null); setPickNote(''); setPadVersion(version => version + 1);
    setDeclining(false); setReason(''); decline.reset(); sign.reset(); void documentQuery.refetch();
  };

  /*
   * 1.50 — a picture of a stamp or a signature, picked through the icon in the
   * pad. It is read here and held until "שמור" puts it in the boxes: the boxes
   * show what will be sent, so a picture that is only picked is not in them.
   */
  const pick = async (file: File) => {
    setPreparingAssets(true);
    setPickNote(t('signing.assets.preparing'));
    try {
      const image = await importSigningImage(file);
      if (image) { setPicked(image); setPickNote(''); }
      else setPickNote(t('signing.assets.tooDetailed'));
    } catch { setPickNote(t('signing.assets.rejected')); }
    finally { setPreparingAssets(false); }
  };

  const document = documentQuery.data;

  useEffect(() => {
    if (!token || !document || blockedToken === token) return;
    let active = true;
    let started = window.document.visibilityState === 'visible' ? Date.now() : null;
    const flush = () => {
      const now = Date.now();
      const seconds = started === null ? 0 : Math.min(60, Math.floor((now - started) / 1000));
      started = window.document.visibilityState === 'visible' ? now : null;
      if (seconds > 0 || window.document.visibilityState === 'visible') void publicRequest(`/sign/${token}/heartbeat`, { method: 'POST', keepalive: true, body: JSON.stringify({ seconds }) }).catch((error: unknown) => {
        if (active && error instanceof RequestError && error.status === 404) setBlockedToken(token);
      });
    };
    const timer = window.setInterval(flush, 15000);
    window.document.addEventListener('visibilitychange', flush);
    window.addEventListener('pagehide', flush);
    return () => { active = false; window.clearInterval(timer); window.document.removeEventListener('visibilitychange', flush); window.removeEventListener('pagehide', flush); };
  }, [token, Boolean(document), blockedToken]);

  if (documentQuery.isLoading) return <LoadingScreen />;

  if (documentQuery.isError || !document || blockedToken === token) {
    return (
      <div className="public-page">
        <div className="public-shell">
          <Card>
            <div className="result-panel">
              <span className="result-panel__icon" aria-hidden>
                🔎
              </span>
              <h1>{t('publicSign.notFound')}</h1>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  /*
   * SIG-003 — "signed" is about the visitor, not only about the document.
   *
   * A document four people sign closes when the last of them does, so the one
   * who signed first would otherwise be shown the signing form again on their
   * own link — and, worse, be told the document is still waiting for them.
   */
  const mine = document.signerPosition;
  const me = document.signers.find((signer) => signer.position === mine);
  const others = document.signers.filter((signer) => signer.position !== mine);
  const waitingOn = others.filter((signer) => !signer.signedAt);
  const signed = document.status === 'signed' || Boolean(me?.signedAt);
  const declined = document.status === 'declined';
  const ownerPreview = document.ownerPreview === true;
  const closed = document.status === 'cancelled' || (document.expired && !signed && !declined);

  return (
    <div className="public-page">
      <div className="public-shell">
        {waitingNotice && waitingOn.length > 0 && <Modal open title={t('publicSign.waitingTitle')} onClose={() => setWaitingNotice(false)} footer={<Button variant="primary" onClick={() => setWaitingNotice(false)}>{t('common.close')}</Button>}>
          <p>{t('publicSign.waitingBody')}</p>
        </Modal>}
        <div className="row row--end">
          <LanguageSwitcher />
        </div>

        <SigningBusinessHeader business={document.business} />
        {ownerPreview && <Card><p role="status">{t('publicSign.creatorSessionNotice')}</p></Card>}

        {signed && (
          <Card>
            <div className="result-panel">
              <span className="result-panel__icon" aria-hidden>
                ✅
              </span>
              <h1>{t('publicSign.signed')}</h1>
              <p className="muted">
                {/* The name this signer was asked under when there are several
                    of them: the document's own signed_by is the first signer's,
                    and thanking the second signer by the first one's name is
                    worse than thanking them by the name on the invitation. */}
                {t('publicSign.signedBody', {
                  name: (document.signers.length > 1 ? me?.name : document.signerName) ?? document.signerName ?? '',
                })}
              </p>
              {/* Whose signature the document is still open for. Said plainly:
                  a signer who is told "signed" and then sees the document
                  unchanged a week later assumes theirs did not take. */}
              {waitingOn.length > 0 && (
                <p className="muted">
                  {t('publicSign.waitingOnOthers', { names: waitingOn.map((signer) => signer.name).join(' · ') })}
                </p>
              )}
              {document.pdfUrl && (
                <Button variant="primary" onClick={() => void platform.openExternal(document.pdfUrl!)}>
                  {t('publicSign.download')}
                </Button>
              )}
            </div>
          </Card>
        )}

        {declined && (
          <Card>
            <div className="result-panel">
              <span className="result-panel__icon" aria-hidden>
                ✋
              </span>
              <h1>{t('publicSign.declined')}</h1>
              <p className="muted">{t('publicSign.declinedBody')}</p>
            </div>
          </Card>
        )}

        {closed && (
          <Card>
            <div className="result-panel">
              <span className="result-panel__icon" aria-hidden>
                ⏳
              </span>
              <h1>{t(document.status === 'cancelled' ? 'publicSign.cancelled' : 'publicSign.expired')}</h1>
              <p className="muted">{t('publicSign.closedBody')}</p>
            </div>
          </Card>
        )}

        <Card>
          <h2>{document.title}</h2>
          {document.signers.length>1&&<ul aria-label={t('publicSign.signersTitle')} className="my-3 space-y-2">
            {document.signers.map(signer=><li key={signer.position} className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2 text-sm">
              <span>{signer.name}</span><span className={signer.signedAt?'text-success':'text-muted-foreground'}>{t(signer.signedAt?'signing.status.signed':'signing.signerWaiting')}</span>
            </li>)}
          </ul>}
          <p className="muted">{t('signing.numberBadge', { number: document.number })} · {new Intl.DateTimeFormat(currentLocale() === 'he' ? 'he-IL' : 'en-GB').format(new Date(document.createdAt))}</p>
          {/* SIG-003 — the file, drawn here rather than linked. A document
              behind a download is a document signed unread on a phone, and the
              boxes on the pages are the answer to "where does my name go". */}
          {document.sourceUrl ? (
            <div className="stack">
              {/* After signing this is the signed copy rather than the original:
                  the boxes were a promise about where the signature would go,
                  and once it is there the promise is not what to show. */}
              {/* No instruction on a document that has been answered: telling
                  somebody to read it carefully before signing, under a panel
                  saying they refused it, reads as if the refusal did not take. */}
              {!declined && (
                <p className="muted">{t(signed ? 'publicSign.signedDocumentHint' : 'publicSign.documentHint')}</p>
              )}
              {/* Said separately from the reading instruction above, because it
                  is an instruction to do something rather than to read: a form
                  with blanks in the middle of it is missed by anyone who came
                  to the page expecting only to sign at the bottom. Only when a
                  blank is this signer's (1.1): the sender's words and the other
                  signers' blanks are not theirs to fill in. */}
              {!signed && !closed && !declined && asksSignerToType(document.anchors, mine) && (
                <p className="muted">{t('publicSign.documentTextHint')}</p>
              )}
              {/* Which boxes are this visitor's, on a document several people
                  sign. Somebody else's is shown greyed out with their name on
                  it: a signer who cannot see that another line is not theirs
                  signs in it. */}
              {!signed && !closed && !declined && document.signers.length > 1 && (
                <p className="muted">{t('publicSign.yourBoxes', { number: mine })}</p>
              )}
              <SignaturePlacer
                key={`${document.contentRevision}-${signed}`}
                className="!max-h-none"
                src={apiUrl(`/public/sign/${token}/${signed ? 'pdf' : 'source.pdf'}`)}
                anchors={signed || declined ? [] : document.anchors}
                values={anchorTexts}
                signerCount={document.signers.length}
                signerNames={Object.fromEntries(document.signers.map((signer) => [signer.position, signer.name]))}
                mine={mine}
                mySignature={signed || closed || declined ? null : preview}
                onValue={signed || closed || declined ? undefined : (index, text) => setAnchorTexts((previous) => ({ ...previous, [String(index)]: text }))}
              />
            </div>
          ) : document.digitalDocument ? (
            <div className="space-y-5 pt-4">
              {!signed && !closed && !declined && <p className="text-sm text-muted-foreground">{t('digitalDocument.fillHint')}</p>}
              <DigitalDocumentView document={document.digitalDocument} values={signed ? document.fieldValues : fieldValues} errors={fieldErrors} disabled={sign.isPending}
                onChange={signed || closed || declined ? undefined : (id, value) => { setFieldValues(previous => ({ ...previous, [id]: value })); setFieldErrors(previous => { const next = { ...previous }; delete next[id]; return next; }); }} />
            </div>
          ) : (
            <p className="prewrap">{document.body}</p>
          )}
        </Card>

        {!signed && !closed && !declined && (
          <Card>
            <div className="stack">
              {/* 1.50 — the pad above the name, with the way in for a picture of
                  a stamp or a signature inside its frame. */}
              <Field label={t('publicSign.drawHere')} hint={t('publicSign.drawHint')}>
              <SignaturePad
                  key={`${token}:${padVersion}`}
                  onChange={setSignature}
                  disabled={sign.isPending || preparingAssets}
                  clearLabel={t('publicSign.clear')}
                  backdrop={uploaded}
                  className="signature-pad !h-auto aspect-[2/1]"
                  upload={{ label: t('publicSign.uploadMark'), onPick: (file) => void pick(file) }}
                />
              </Field>
              {pickNote && <p role="status" className="text-sm">{pickNote}</p>}
              {shownMark && (
                <PickedMark
                  image={shownMark}
                  waiting={picked !== null}
                  disabled={sign.isPending || preparingAssets}
                  onSave={() => { setUploaded(picked); setPicked(null); }}
                  onRemove={() => (picked !== null ? setPicked(null) : setUploaded(null))}
                />
              )}
              <Field htmlFor='signature-signer-name' label={t('publicSign.signerName')}>
                <Input id="signature-signer-name" className="min-h-12 text-base" value={signerName} onChange={(event) => setSignerName(event.target.value)} />
              </Field>

              {sign.isError && <>
                <ErrorNotice code={(sign.error as RequestError).code === 'errors.conflict' ? 'publicSign.revisionChanged' : (sign.error as RequestError).code} />
                {(sign.error as RequestError).code === 'errors.conflict' && <Button onClick={reloadLatest}>{t('publicSign.reloadLatest')}</Button>}
              </>}

              <Button
                variant="primary"
                loading={sign.isPending}
                disabled={signerName.trim().length < 2 || !preview || stylingSignature || preparingAssets || picked !== null}
                onClick={() => {
                  const errors = validateDocumentValues(document.digitalDocument, fieldValues);
                  setFieldErrors(errors);
                  const first = Object.keys(errors)[0];
                  if (first) { window.document.querySelector<HTMLElement>(`[data-document-field="${first}"]`)?.focus(); return; }
                  sign.mutate();
                }}
              >
                {t('publicSign.submit')}
              </Button>

              {/* Under the signature and quieter than it, because refusing is
                  the rarer answer — and offering it as an equal button beside
                  "sign" invites a decision nobody came here to make. */}
              {declining ? (
                <div className="stack">
                  <Field label={t('publicSign.declineReason')} hint={t('publicSign.declineReasonHint')}>
                    <Input value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500} />
                  </Field>
                  {decline.isError && <>
                    <ErrorNotice code={(decline.error as RequestError).code === 'errors.conflict' ? 'publicSign.revisionChanged' : (decline.error as RequestError).code} />
                    {(decline.error as RequestError).code === 'errors.conflict' && <Button onClick={reloadLatest}>{t('publicSign.reloadLatest')}</Button>}
                  </>}
                  <div className="row row--wrap">
                    <Button variant="danger" loading={decline.isPending} onClick={() => decline.mutate()}>
                      {t('publicSign.declineConfirm')}
                    </Button>
                    <Button variant="ghost" onClick={() => setDeclining(false)}>
                      {t('common.cancel')}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button variant="ghost" size="sm" onClick={() => setDeclining(true)}>
                  {t('publicSign.decline')}
                </Button>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
